import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  FaPlus, FaEdit, FaTrash, FaSitemap, FaBolt, FaLink, FaUsers, FaSnowflake, FaPlay, FaBan, FaUserPlus,
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import TableView from './TableView';
import CandidatosKanbanPanel from './CandidatosKanbanPanel';
import AddVacanteModal from './AddVacanteModal';
import AssignEmployeeToVacanteModal from './AssignEmployeeToVacanteModal';
import NewEmployeeModal from './NewEmployeeModal';
import { QuickActionsMenu } from './common';
import { useAuth } from '../context/AuthContext';
import {
  VACANTES_ADMIN_ROLES, ESTADOS_VIGENTES, estadoClase, asientoClase, diasDesde, diasClase,
  formatFecha, iniciales, rutaPuesto,
} from './vacantesShared';
import './VacantesView.css';
import './VacantesUI.css';

const EN_HISTORIAL = ['Cubierta', 'Cancelada'];
const ORDEN_PRIORIDAD = { Alta: 1, Media: 2, Baja: 3 };

const VacantesView = () => {
  const { user } = useAuth();
  const canManage = VACANTES_ADMIN_ROLES.includes(user?.role);

  const [vacantes, setVacantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('vigentes');
  const [columnVisibility, setColumnVisibility] = useState({ gerencia: false, departamento: false });

  const [menu, setMenu] = useState(null); // { id, rect }
  const [editando, setEditando] = useState(null); // null | 'nueva' | vacante
  const [enlazando, setEnlazando] = useState(null);
  const [ingresando, setIngresando] = useState(null);
  const [candidatosDe, setCandidatosDe] = useState(null);

  const fetchVacantes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/vacantes');
      setVacantes(response.data);
      setError(null);
    } catch (err) {
      setError('No se pudieron cargar las vacantes');
      console.error('Error fetching vacantes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVacantes();
  }, [fetchVacantes]);

  // Cerrar el menu de acciones al hacer clic fuera
  useEffect(() => {
    if (!menu) return undefined;
    const cerrar = (e) => {
      if (!e.target.closest('.quick-actions-menu') && !e.target.closest('.vac-menu-trigger')) setMenu(null);
    };
    document.addEventListener('mousedown', cerrar);
    return () => document.removeEventListener('mousedown', cerrar);
  }, [menu]);

  const cambiarEstado = useCallback(async (vacante, estado, confirmacion) => {
    setMenu(null);
    if (confirmacion && !window.confirm(confirmacion)) return;
    try {
      await axiosInstance.patch(`/api/vacantes/${vacante.id}/estado`, { estado });
      fetchVacantes();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cambiar el estado');
    }
  }, [fetchVacantes]);

  const eliminar = useCallback(async (vacante) => {
    setMenu(null);
    if (!window.confirm(`¿Eliminar la vacante "${vacante.titulo}"? Úsalo solo si se abrió por error; si el puesto ya no se va a cubrir, cancélala.`)) return;
    try {
      await axiosInstance.delete(`/api/vacantes/${vacante.id}`);
      fetchVacantes();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar la vacante');
    }
  }, [fetchVacantes]);

  const verEnOrganigrama = useCallback((vacante) => {
    setMenu(null);
    const params = new URLSearchParams({
      vacante: vacante.id,
      gerencia: vacante.gerencia_id || '',
      departamento: vacante.departamento_id || '',
      area: vacante.area_id || ''
    });
    window.open(`/organigrama?${params.toString()}`, '_blank');
  }, []);

  const vigentes = useMemo(() => vacantes.filter((v) => !EN_HISTORIAL.includes(v.estado)), [vacantes]);
  const historial = useMemo(() => vacantes.filter((v) => EN_HISTORIAL.includes(v.estado)), [vacantes]);
  const porEnlazar = useMemo(() => vigentes.filter((v) => v.sugerencias_enlace?.length), [vigentes]);

  const filas = useMemo(() => {
    if (activeTab === 'historial') {
      return [...historial].sort((a, b) => String(b.fecha_cierre || '').localeCompare(String(a.fecha_cierre || '')));
    }
    // Primero lo que ya parece cubierto (hay que enlazarlo), luego por prioridad y antigüedad
    return [...vigentes].sort((a, b) =>
      Number(!!b.sugerencias_enlace?.length) - Number(!!a.sugerencias_enlace?.length)
      || (ORDEN_PRIORIDAD[a.prioridad] || 4) - (ORDEN_PRIORIDAD[b.prioridad] || 4)
      || String(a.fecha_creacion).localeCompare(String(b.fecha_creacion)));
  }, [activeTab, vigentes, historial]);

  const availableFilters = useMemo(() => {
    const base = activeTab === 'historial' ? historial : vigentes;
    return [
      { id: 'gerencia', placeholder: 'Todas las gerencias' },
      {
        id: 'departamento',
        placeholder: 'Todos los departamentos',
        options: [...new Set(base.map((v) => v.departamento).filter(Boolean))],
      },
    ];
  }, [activeTab, vigentes, historial]);

  const accionesMenu = useCallback((v) => {
    const items = [];
    if (canManage) {
      items.push({ icon: FaUsers, label: 'Candidatos', color: '#2563eb', onClick: () => { setMenu(null); setCandidatosDe(v); } });
      items.push({ icon: FaLink, label: 'Enlazar a quien ya lo ocupa', color: '#059669', onClick: () => { setMenu(null); setEnlazando(v); } });
      items.push({ icon: FaEdit, label: 'Editar', color: '#4b5563', onClick: () => { setMenu(null); setEditando(v); } });
      if (v.estado === 'Congelada') {
        items.push({ icon: FaPlay, label: 'Reactivar', color: '#d97706', onClick: () => cambiarEstado(v, 'Abierta') });
      } else {
        items.push({ icon: FaSnowflake, label: 'Congelar', color: '#0284c7', onClick: () => cambiarEstado(v, 'Congelada') });
      }
      items.push({
        icon: FaBan, label: 'Cancelar vacante', color: '#6b7280',
        onClick: () => cambiarEstado(v, 'Cancelada', `¿Cancelar "${v.titulo}"? El puesto deja de buscarse y sale del organigrama.`),
      });
    }
    items.push({ icon: FaSitemap, label: 'Ver en organigrama', color: '#7c3aed', onClick: () => verEnOrganigrama(v) });
    if (canManage) {
      items.push({ icon: FaTrash, label: 'Eliminar', color: '#dc2626', onClick: () => eliminar(v) });
    }
    return items;
  }, [canManage, cambiarEstado, eliminar, verEnOrganigrama]);

  const columnaPuesto = useMemo(() => ({
    header: 'Puesto',
    accessorKey: 'titulo',
    size: 360,
    cell: ({ row }) => {
      const v = row.original;
      const sugerencia = v.sugerencias_enlace?.[0];
      return (
        <div className="vac-puesto-cell">
          <span className={asientoClase(v.estado)} title={v.estado}>
            {v.estado === 'Cubierta' ? iniciales(v.empleado_que_surte) : ''}
          </span>
          <div className="vac-puesto-cell-texto">
            <span className="vac-puesto-titulo">{v.titulo}</span>
            <span className="vac-puesto-ruta">{rutaPuesto(v)}</span>
            {sugerencia && canManage && (
              <span className="vac-sugerencia">
                <span>
                  {sugerencia.nombre} ingresó a este cargo el {formatFecha(sugerencia.fecha_ingreso)}
                  {v.sugerencias_enlace.length > 1 ? ` (y ${v.sugerencias_enlace.length - 1} más)` : ''}
                </span>
                <button type="button" onClick={() => setEnlazando(v)}>Enlazar</button>
              </span>
            )}
          </div>
        </div>
      );
    }
  }), [canManage]);

  const columnasOcultas = useMemo(() => ([
    { header: 'Gerencia', accessorKey: 'gerencia' },
    { header: 'Departamento', accessorKey: 'departamento' },
  ]), []);

  const columnaEstado = useMemo(() => ({
    header: 'Estado',
    accessorKey: 'estado',
    size: 130,
    cell: ({ getValue }) => <span className={estadoClase(getValue())}>{getValue()}</span>
  }), []);

  const columnasVigentes = useMemo(() => [
    columnaPuesto,
    columnaEstado,
    {
      header: 'Motivo',
      accessorKey: 'motivo',
      size: 130,
      cell: ({ getValue }) => <span className="text-sm text-gray-600">{getValue()}</span>
    },
    {
      header: 'Reporta a',
      accessorKey: 'supervisor_nombre',
      size: 190,
      cell: ({ getValue }) => <span className="text-sm text-gray-700">{getValue() || 'Sin supervisor'}</span>
    },
    {
      header: 'Abierta hace',
      id: 'dias',
      accessorFn: (v) => diasDesde(v.fecha_creacion),
      size: 120,
      cell: ({ getValue }) => {
        const dias = getValue();
        return <span className={diasClase(dias)}>{dias === 1 ? '1 día' : `${dias} días`}</span>;
      }
    },
    {
      header: 'Prioridad',
      accessorKey: 'prioridad',
      size: 100,
      sortingFn: (a, b, id) => (ORDEN_PRIORIDAD[a.getValue(id)] || 4) - (ORDEN_PRIORIDAD[b.getValue(id)] || 4),
      cell: ({ getValue }) => <span className="text-sm text-gray-600">{getValue()}</span>
    },
    {
      header: 'Candidatos',
      accessorKey: 'candidatos_activos_count',
      size: 170,
      cell: ({ row, getValue }) => {
        const total = getValue() || 0;
        if (!canManage) return <span className="text-sm text-gray-600">{total}</span>;
        const abrir = () => setCandidatosDe(row.original);
        if (!total) {
          return (
            <button type="button" className="vac-cand vac-cand--vacio" onClick={abrir}>
              <FaUserPlus aria-hidden="true" /> Agregar candidatos
            </button>
          );
        }
        const c = row.original.conteo_candidatos || {};
        const etapas = [
          { n: c.nuevos || 0, color: '#94a3b8', nombre: 'nuevos' },
          { n: c.entrevistados || 0, color: '#8b5cf6', nombre: 'entrevistados' },
          { n: c.seleccionados || 0, color: '#10b981', nombre: 'seleccionados' },
        ];
        return (
          <button
            type="button"
            className="vac-cand"
            onClick={abrir}
            title={etapas.map((e) => `${e.n} ${e.nombre}`).join(', ')}
          >
            <span className="vac-cand-linea">
              <FaUsers aria-hidden="true" /> {total === 1 ? '1 candidato' : `${total} candidatos`}
            </span>
            <span className="vac-cand-barra" aria-hidden="true">
              {etapas.filter((e) => e.n).map((e) => (
                <span key={e.nombre} style={{ width: `${(e.n / total) * 100}%`, background: e.color }} />
              ))}
            </span>
            {c.seleccionados > 0 && (
              <span className="vac-cand-listo">
                {c.seleccionados === 1 ? '1 seleccionado' : `${c.seleccionados} seleccionados`}
              </span>
            )}
          </button>
        );
      }
    },
    {
      id: 'acciones',
      header: 'Acciones',
      size: 210,
      cell: ({ row }) => {
        const v = row.original;
        const abierto = menu?.id === v.id;
        return (
          <div className="flex items-center gap-2">
            {ESTADOS_VIGENTES.includes(v.estado) ? (
              <button type="button" className="btn btn-primary btn-sm whitespace-nowrap" onClick={() => setIngresando(v)}>
                <FaUserPlus /> Registrar ingreso
              </button>
            ) : (
              <span className="text-xs text-gray-500 px-1">Congelada: reactívala para el ingreso</span>
            )}
            <button
              type="button"
              className={`icon-button vac-menu-trigger ${abierto ? 'active' : ''}`}
              aria-label="Más acciones"
              title="Más acciones"
              onClick={(e) => setMenu(abierto ? null : { id: v.id, rect: e.currentTarget.getBoundingClientRect() })}
            >
              <FaBolt aria-hidden="true" />
            </button>
            <AnimatePresence>
              {abierto && (
                <QuickActionsMenu anchorRect={menu.rect}>
                  {accionesMenu(v).map((a) => (
                    <button key={a.label} className="quick-action-item" onClick={a.onClick}>
                      <span className="action-icon" style={{ color: a.color }}><a.icon /></span>
                      <span className="action-label">{a.label}</span>
                    </button>
                  ))}
                </QuickActionsMenu>
              )}
            </AnimatePresence>
          </div>
        );
      }
    },
    ...columnasOcultas,
  ], [columnaPuesto, columnaEstado, columnasOcultas, canManage, menu, accionesMenu]);

  const columnasHistorial = useMemo(() => [
    columnaPuesto,
    columnaEstado,
    {
      header: 'Motivo',
      accessorKey: 'motivo',
      size: 130,
      cell: ({ getValue }) => <span className="text-sm text-gray-600">{getValue()}</span>
    },
    {
      header: 'Cubierta por',
      accessorKey: 'empleado_que_surte',
      size: 200,
      cell: ({ getValue }) => (getValue()
        ? <span className="text-sm font-medium text-emerald-700">{getValue()}</span>
        : <span className="text-sm text-gray-400">Nadie</span>)
    },
    {
      header: 'Abierta',
      accessorKey: 'fecha_creacion',
      size: 110,
      cell: ({ getValue }) => <span className="text-sm text-gray-600 tabular-nums">{formatFecha(getValue())}</span>
    },
    {
      header: 'Cerrada',
      accessorKey: 'fecha_cierre',
      size: 110,
      cell: ({ getValue }) => <span className="text-sm text-gray-600 tabular-nums">{formatFecha(getValue()) || '—'}</span>
    },
    ...columnasOcultas,
  ], [columnaPuesto, columnaEstado, columnasOcultas]);

  const tabs = [
    { key: 'vigentes', label: 'Vigentes', count: vigentes.length },
    { key: 'historial', label: 'Historial', count: historial.length },
  ];

  return (
    <div className="vacantes-view">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
        <h2 className="page-title">Vacantes</h2>
        <div className="header-nav">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`nav-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.key); setMenu(null); }}
            >
              {tab.label}
              <span style={{ marginLeft: '6px', opacity: 0.7 }}>({tab.count})</span>
            </button>
          ))}
        </div>
        {canManage && (
          <button className="btn-primary" onClick={() => setEditando('nueva')}>
            <FaPlus /> Abrir vacante
          </button>
        )}
      </div>

      {activeTab === 'vigentes' && canManage && porEnlazar.length > 0 && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {porEnlazar.length === 1
            ? 'Una vacante parece cubierta: alguien ingresó a ese cargo mientras seguía abierta. Enlázala para cerrarla.'
            : `${porEnlazar.length} vacantes parecen cubiertas: alguien ingresó a esos cargos mientras seguían abiertas. Enlázalas para cerrarlas.`}
        </div>
      )}

      <TableView
        key={activeTab}
        data={filas}
        columns={activeTab === 'historial' ? columnasHistorial : columnasVigentes}
        loading={loading}
        error={error}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        defaultPageSize={30}
        availableFilters={availableFilters}
        hideColumnSettings={true}
      />

      <AnimatePresence>
        {candidatosDe && (
          <CandidatosKanbanPanel
            vacante={candidatosDe}
            onClose={() => {
              setCandidatosDe(null);
              fetchVacantes(); // cargar candidatos pasa la vacante a En proceso
            }}
            onVacanteUpdated={fetchVacantes}
          />
        )}
      </AnimatePresence>

      {editando && (
        <AddVacanteModal
          vacante={editando === 'nueva' ? null : editando}
          onClose={() => setEditando(null)}
          onSuccess={() => {
            setEditando(null);
            fetchVacantes();
          }}
        />
      )}

      {enlazando && (
        <AssignEmployeeToVacanteModal
          vacante={enlazando}
          onClose={() => setEnlazando(null)}
          onSuccess={() => {
            setEnlazando(null);
            fetchVacantes();
          }}
        />
      )}

      {ingresando && (
        <NewEmployeeModal
          initialVacanteId={ingresando.id}
          onClose={() => setIngresando(null)}
          onEmployeeAdded={() => {
            setIngresando(null);
            fetchVacantes();
          }}
        />
      )}
    </div>
  );
};

export default VacantesView;
